// Copyright (c) StrangeBee 2024

import React, { ReactElement, useCallback, useContext, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { trimTags, useLocalMessage } from '@Helpers/';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import RouteBuilder from '@Services/RouteBuilder';
import {
    SharedOrgOutput,
    AlertOutput,
    AlertStatusMapType,
    AlertStatusOutput,
    CustomStatusMapType,
    AlertStatusStage,
    PermissionsEnum,
} from 'thehive-sdk';
import { THForm } from '@Molecules/THForm/THForm';
import { Form, Space } from 'antd';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import Details from '@Molecules/Details/Details';
import useListQuery from '@Hooks/useListQuery';
import client from '@Client';
import { FormatDate } from '@Molecules/Format';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import { DisplayMetadata } from '@Molecules/DisplayMetadata/DisplayMetadata';
import { AlertEventList } from '@Organisms/Alert/eventList';
import { extractData } from '@Organisms/Alert/AlertDrawer/extractData';
import { CopyValue } from '@Atoms/CopyValue/CopyValue';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { ICONS } from '@Constants/iconsApp';
import { ListActionType } from '@Types';
import { mergeCF } from '@Helpers/customField';
import { mergeTag } from '@Helpers/array';
import { ScopeContext } from '@Context/ScopeProvider';
import { PermissionExtract } from '@Hooks/useSplitPermissions';
import useCustomQuery from '@Hooks/useCustomQuery';
import { useListAllUsers } from '@Queries/userQueries';

import './AlertDrawer.scss';

const cycleStage = [AlertStatusStage.NEW, AlertStatusStage.IN_PROGRESS, AlertStatusStage.CLOSED];

interface AlertDrawerProps {
    data: AlertOutput;
    eventList?: AlertEventList;
    triggerElem?: ReactElement;
    listShared?: SharedOrgOutput[];
    defaultVisible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
    hasPermissions?: Record<PermissionExtract<[PermissionsEnum.MANAGE_ALERT_UPDATE]>, boolean>;
    visible?: boolean;
    afterVisible?: (visible: boolean) => void;
    actionList?: Record<string, ListActionType>;
}

export const AlertDrawer = ({
    data,
    eventList,
    triggerElem,
    defaultVisible = false,
    onVisibleChange,
    hasPermissions,
    visible,
    afterVisible,
    actionList,
}: AlertDrawerProps): ReactElement => {
    const i = useLocalMessage();
    const [form] = Form.useForm();

    const [currentData, setCurrentData] = useState<AlertOutput>(data);
    const [updatedData, setUpdatedData] = useState<boolean>(false);
    const [forceUpdate, setForceUpdate] = useState<boolean>(false);

    const {
        _id: alertId,
        severity = 1,
        tlp,
        pap,
        _createdBy,
        _createdAt,
        source,
        sourceRef,
        type,
        extraData,
        caseId,
        stage,
        observableCount,
        _updatedAt,
        _updatedBy,
    } = currentData;

    const { importDate, caseNumber, procedureCount } = extraData || {};
    const history = useHistory();

    const [fieldsTouched, setFieldsTouched] = useState(false);
    const [editMode, setEditMode] = React.useState(false);
    const [activeKeyCF, setActiveKeyCF] = useState('0');
    const [alertStatus, setAlertStatus] = React.useState<CustomStatusMapType>({});

    const { data: alertStatusList = [], refetch: refetchStatusList } = useListQuery<AlertStatusOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'listAlertStatus' }],
                extraData: ['canDelete'],
                name: 'get-all-alerts-status',
            },
            sort: ['-stage', '+value'],
        },
        controller: client.CustomStatusController,
        fetchOnStart: false,
        manual: true,
    });

    const { data: similarAlertCount } = useCustomQuery<number>({
        queryParams: {
            selector: {
                head: [{ name: 'getAlert', idOrName: alertId }, { name: 'similarAlertCount' }],
                name: 'get-alert-similar-alert-count',
            },
        },
        controller: client.AlertController,
        fetchOnStart: true,
        manual: true,
    });

    const { data: similarCaseCount } = useCustomQuery<number>({
        queryParams: {
            selector: {
                head: [{ name: 'getAlert', idOrName: alertId }, { name: 'similarCaseCount' }],
                name: 'get-alert-similar-case-count',
            },
        },
        controller: client.AlertController,
        fetchOnStart: true,
        manual: true,
    });

    const { customFieldList: customFields } = useContext(ScopeContext);

    const { data: assignableUsers = [], refetch: refetchAssignableUsers } = useListAllUsers();

    const editAlert = useCallback(
        ({ customFields, tags, ...values }) => {
            const treatedCF = customFields
                ? mergeCF(currentData?.customFields || [], data?.customFields || [], customFields)
                : [];

            const treatedTag = trimTags(tags ? mergeTag(currentData?.tags || [], data?.tags || [], tags) : []);
            if (eventList) {
                eventList
                    .onEdit(
                        {
                            ...values,
                            ...(customFields ? { customFields: treatedCF } : {}),
                            ...(tags ? { tags: treatedTag } : {}),
                            assignee: 'assignee' in values && !values.assignee ? null : values.assignee,
                        },
                        alertId,
                        stage === AlertStatusStage.NEW,
                    )
                    .finally(() => {
                        setForceUpdate(true);
                        setFieldsTouched(false);
                        setEditMode(false);
                    });
            } else {
                setForceUpdate(true);
                setFieldsTouched(false);
                setEditMode(false);
            }
        },
        [data, alertId, currentData, eventList, stage],
    );

    useEffect(() => {
        if (alertStatusList.length) {
            const stageIndex = cycleStage.indexOf((stage || '') as AlertStatusStage);
            const alertStatusMap = alertStatusList.reduce((acc, curr) => {
                const stagePosition = cycleStage.indexOf((curr.stage || '') as AlertStatusStage);
                if (stagePosition !== stageIndex) {
                    return acc;
                }

                if (!acc[curr.stage]) {
                    acc[curr.stage] = [];
                }
                acc[curr.stage].push(curr);
                return acc;
            }, {} as AlertStatusMapType);
            setAlertStatus(alertStatusMap);
        }
    }, [alertStatusList, stage]);

    useEffect(() => {
        form.setFieldsValue(currentData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentData]);

    useEffect(() => {
        if (!editMode && updatedData) {
            setCurrentData(data);
            setUpdatedData(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editMode, updatedData]);

    useEffect(() => {
        if ((!editMode && data) || forceUpdate) {
            setCurrentData(data);
            setForceUpdate(false);
        } else {
            setUpdatedData(true);
        }
    }, [data, editMode, forceUpdate, form]); // eslint-disable-line

    useEffect(() => {
        refetchStatusList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <THDrawer
            title={
                <Space>
                    <ICONS.AlertIcon />
                    {i('alert.preview')}
                </Space>
            }
            triggerElem={triggerElem}
            className="drawer-form"
            width="55vw"
            disableClose={fieldsTouched}
            visible={visible}
            afterVisibleChange={(visible) => {
                form.resetFields();
                onVisibleChange?.(visible);
                afterVisible?.(visible);
            }}
            defaultVisible={defaultVisible}
        >
            <THForm<AlertOutput>
                name="alert-drawer-form"
                form={form}
                editable
                initialValues={{
                    ...currentData,
                    customFields: currentData?.customFields.sort(({ value: valueA }, { value: valueB }) =>
                        `${valueA}`.localeCompare(`${valueB}`),
                    ),
                }}
                onValuesChange={() => {
                    setEditMode(true);
                    setFieldsTouched(true);
                }}
                //eslint-disable-next-line
                onFinish={editAlert}
                submitTouchedValues={['customFields']}
            >
                <THForm.Layout
                    header={
                        <DisplayMetadata
                            items={[
                                {
                                    icon: <ICONS.SettingIcon />,
                                    label: 'id',
                                    value: <CopyValue value={alertId}>{alertId}</CopyValue>,
                                },
                                {
                                    icon: <ICONS.UserIcon />,
                                    label: i('common.created-by'),
                                    value: <AssigneeInfo assignee={_createdBy} noAvatar />,
                                },
                                {
                                    icon: <ICONS.CreationDateIcon />,
                                    label: i('common.created-at'),
                                    value: <FormatDate date={_createdAt} />,
                                },
                                {
                                    icon: <ICONS.UserIcon />,
                                    label: i('common.reviewed-by'),
                                    value: <AssigneeInfo assignee={_updatedBy} noAvatar />,
                                    hidden: !_updatedBy,
                                },
                                {
                                    icon: <ICONS.UpdateDateIcon />,
                                    label: i('common.reviewed-at'),
                                    value: <FormatDate date={_updatedAt} />,
                                    hidden: !_updatedAt,
                                },
                                {
                                    icon: <ICONS.DateIcon />,
                                    label: i('common.import-date'),
                                    value: <FormatDate date={importDate} />,
                                    hidden: !importDate,
                                },
                            ]}
                        />
                    }
                    footer={
                        editMode ? (
                            <FormSubmitButton
                                onCancel={() => {
                                    form.resetFields();
                                    setFieldsTouched(false);
                                    setForceUpdate(true);
                                    setEditMode(false);
                                }}
                                disabled={false}
                                footerText={i('general.confirm')}
                                hidden={!fieldsTouched}
                                formName="alert-drawer-form"
                            />
                        ) : (
                            <THDrawer.Footer
                                actions={
                                    eventList &&
                                    Object.values(hasPermissions || {}).some(Boolean) && (
                                        <ActionsList
                                            actionList={{
                                                ...actionList,
                                                unlink: {
                                                    label: i('alert.actions.unlink.label'),
                                                    icon: <ICONS.LinkOutlined />,
                                                    hidden: !caseId,
                                                    message: i('alert.actions.unlink.confirm'),
                                                    confirm: true,
                                                    query: () => eventList?.onUnlinkFromCase(alertId, caseId || ''),
                                                    scope: 'both',
                                                },
                                            }}
                                            scope="single"
                                        />
                                    )
                                }
                            >
                                {
                                    <Link
                                        to={RouteBuilder.compute('alerts.details', {
                                            alertId: data?._id || '-',
                                        })}
                                    >
                                        <THButton type="primary">{i('common.goToDetails')}</THButton>
                                    </Link>
                                }
                            </THDrawer.Footer>
                        )
                    }
                >
                    <Details
                        data={{
                            ...data,
                            '#computeMetadata': {
                                type,
                                source,
                            },
                            '#computeDate': {
                                reference: sourceRef,
                            },
                            '#computeCount': {
                                observableCount,
                                procedureCount,
                                similarAlertCount,
                                similarCaseCount,
                            },
                            '#computeSecurity': {
                                tlp,
                                pap,
                                severity,
                            },
                            '#computeAssignee': {
                                assignableUsers,
                                refetchAssignableUsers,
                            },
                            '#computeStatus': {
                                statusList: alertStatus,
                                refetchStatusList,
                                isImported: stage === AlertStatusStage.IMPORTED,
                            },
                            '#computeCustomFields': {
                                customFields,
                                form,
                                activeKeyState: {
                                    activeKey: activeKeyCF,
                                    setActiveKey: setActiveKeyCF,
                                },
                            },
                            '#computeCase': caseNumber
                                ? {
                                      caseNumber,
                                      onClick: () => history.push(RouteBuilder.compute('cases.details', { caseId })),
                                  }
                                : undefined,
                            importDate,
                        }}
                        extract={extractData({
                            hasPermissions,
                            data,
                            eventList,
                            locale: i,
                            formEditMode: editMode,
                        })}
                    />
                </THForm.Layout>
            </THForm>
        </THDrawer>
    );
};
